import { useState, type ReactNode } from 'react';

import { api } from '../../api';
import { Aviso, Campo, Estado, Etiqueta, Painel, data, useAcao, useCarga } from '../../comum';
import {
  ROTULO_TIPO,
  TIPOS_DE_EVENTO,
  type ConfigCatalogo,
  type DestinoPublico,
  type TipoDestino,
} from './tipos';

/**
 * O catálogo de software proibido e o formulário de destino.
 *
 * Separados da tela porque são as duas peças que escrevem: uma marca o que é
 * violação, a outra diz para onde a notificação vai. O resto de `Integracoes`
 * só lista.
 */

export function resumirDestino(d: DestinoPublico): string {
  const c = d.config;
  if (d.tipo === 'jira') return `${String(c['baseUrl'] ?? '')} · ${String(c['projeto'] ?? '')}`;
  if (d.tipo === 'webhook') return String(c['url'] ?? '');
  if (d.tipo === 'teams') return 'fluxo do Power Automate';
  if (d.tipo === 'email') {
    const para = Array.isArray(c['destinatarios']) ? (c['destinatarios'] as string[]) : [];
    return `${String(c['host'] ?? '')} → ${para.join(', ')}`;
  }
  return `fila ${String(c['filaIdNoKace'] ?? '?')}`;
}

/* ── Catálogo ────────────────────────────────────────────────────────────── */

export function CatalogoProibidos({
  carga,
  podeEditar,
  acao,
  aoVarrer,
}: {
  carga: ReturnType<typeof useCarga<ConfigCatalogo>>;
  podeEditar: boolean;
  acao: ReturnType<typeof useAcao>;
  aoVarrer: () => void;
}): ReactNode {
  const atual = carga.dados?.configuracao.catalogo;
  const [proibidos, setProibidos] = useState<string | null>(null);
  const [tolerados, setTolerados] = useState<string | null>(null);

  const texto = (v: readonly string[] | undefined): string => (v ?? []).join('\n');
  const emLista = (s: string): string[] =>
    s
      .split('\n')
      .map((x) => x.trim())
      .filter((x) => x !== '');

  return (
    <Painel
      titulo="Software não permitido"
      sub="Um nome por linha. A comparação ignora maiúsculas e casa por trecho contido."
    >
      <Estado carga={carga}>
        {atual !== undefined && (
          <>
            <div className="grade2">
              <Campo
                rotulo="Não permitidos"
                ajuda='Casa por trecho: "torrent" pega "BitTorrent 7.11" e "uTorrent Web".'
              >
                <textarea
                  rows={7}
                  disabled={!podeEditar}
                  value={proibidos ?? texto(atual.softwareNaoPermitido)}
                  onChange={(e) => setProibidos(e.target.value)}
                  placeholder={'BitTorrent\nuTorrent\nTeamViewer'}
                />
              </Campo>
              <Campo
                rotulo="Tolerados"
                ajuda="Vence a lista acima. É a saída para a exceção legítima que a comparação por trecho pega."
              >
                <textarea
                  rows={7}
                  disabled={!podeEditar}
                  value={tolerados ?? texto(atual.softwareTolerado)}
                  onChange={(e) => setTolerados(e.target.value)}
                  placeholder={'TeamViewer Host'}
                />
              </Campo>
            </div>

            <Aviso tipo="alerta">
              A comparação por trecho erra para o lado de <strong>alertar demais</strong>, não de
              deixar passar. Foi uma escolha: no lab, o programa aparece como
              <span className="mono"> CIR: Process Explorer</span> — nem igualdade nem prefixo
              pegariam.
            </Aviso>

            {podeEditar && (
              <div className="linha">
                <button
                  className="primario encolhe"
                  disabled={acao.executando}
                  onClick={() =>
                    void acao.rodar(async () => {
                      await api.put('/api/config/catalogo', {
                        softwareNaoPermitido: emLista(
                          proibidos ?? texto(atual.softwareNaoPermitido),
                        ),
                        softwareTolerado: emLista(tolerados ?? texto(atual.softwareTolerado)),
                      });
                      carga.recarregar();
                      return 'Catálogo salvo.';
                    })
                  }
                >
                  Salvar catálogo
                </button>
                <button
                  className="encolhe"
                  disabled={acao.executando}
                  onClick={() =>
                    void acao.rodar(async () => {
                      const r = await api.post<{
                        detectados: number;
                        novas: number;
                        repetidas: number;
                        entregues: number;
                        destinos: number;
                      }>('/api/integracoes/varrer-software');
                      aoVarrer();
                      return (
                        `${r.detectados} ocorrência(s) no parque · ${r.novas} nova(s) · ` +
                        `${r.entregues} entregue(s)` +
                        (r.repetidas > 0
                          ? ` · ${r.repetidas} já haviam sido entregues antes`
                          : '') +
                        (r.destinos === 0 ? ' — nenhum destino configurado ainda' : '')
                      );
                    })
                  }
                >
                  Varrer o parque agora
                </button>
                <span className="cresce" />
              </div>
            )}
          </>
        )}
      </Estado>
    </Painel>
  );
}

/* ── Formulário ──────────────────────────────────────────────────────────── */

export function FormularioDestino({
  aoCancelar,
  aoSalvar,
  aoPerderSessao,
}: {
  aoCancelar: () => void;
  aoSalvar: () => void;
  aoPerderSessao: () => void;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [tipo, setTipo] = useState<TipoDestino>('kace_ticket');
  /**
   * O que este destino recebe.
   *
   * 🔴 Vazio significa **todos** — e a tela precisa dizer isso, senão a pessoa
   * marca um item achando que está acrescentando e na verdade está restringindo
   * tudo o mais. Antes do filtro existir, todo evento ia para todo destino.
   */
  const [tiposEscolhidos, setTiposEscolhidos] = useState<ReadonlySet<string>>(new Set());
  const [severidadeMinima, setSeveridadeMinima] = useState('baixa');
  const [nome, setNome] = useState('');
  const [c, setC] = useState<Record<string, string>>({});

  const campo = (k: string): string => c[k] ?? '';
  const set = (k: string, v: string): void => setC({ ...c, [k]: v });

  const nuvem = tipo === 'jira' && /\.atlassian\.net/i.test(campo('baseUrl'));

  const salvar = (): void => {
    void acao.rodar(async () => {
      const config: Record<string, unknown> =
        tipo === 'teams'
          ? { url: campo('url'), urlDoPortal: campo('urlDoPortal') || null }
          : tipo === 'email'
            ? {
                host: campo('host'),
                porta: Number(campo('porta') || '587'),
                seguranca: campo('seguranca') || 'starttls',
                usuario: campo('usuario') || null,
                senha: campo('senha') || null,
                remetente: campo('remetente'),
                nomeRemetente: campo('nomeRemetente') || 'Plataforma Lenanzo',
                prefixoAssunto: campo('prefixoAssunto'),
                destinatarios: campo('destinatarios')
                  .split('\n')
                  .map((x) => x.trim())
                  .filter((x) => x !== ''),
              }
            : tipo === 'kace_ticket'
              ? {
                  filaIdNoKace: Number(campo('filaIdNoKace')),
                  aprovadorIdNoKace: null,
                  categoriaIdNoKace: null,
                }
              : tipo === 'jira'
                ? {
                    baseUrl: campo('baseUrl'),
                    projeto: campo('projeto'),
                    tipoIssue: campo('tipoIssue') || 'Task',
                    usuario: campo('usuario'),
                    token: campo('token'),
                    rotulos: campo('rotulos')
                      .split(',')
                      .map((x) => x.trim())
                      .filter((x) => x !== ''),
                  }
                : {
                    url: campo('url'),
                    metodo: 'POST',
                    cabecalhos: {},
                    segredoHmac: campo('segredoHmac'),
                  };

      await api.post('/api/integracoes/destinos', {
        nome,
        tipo,
        config,
        tiposDeEvento: [...tiposEscolhidos],
        severidadeMinima,
      });
      aoSalvar();
      return 'Destino criado.';
    });
  };

  return (
    <Painel titulo="Novo destino">
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}

      <div className="grade2">
        <Campo rotulo="Nome">
          <input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Fila de Segurança"
          />
        </Campo>
        <Campo rotulo="Tipo">
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoDestino)}>
            <option value="kace_ticket">Chamado no KACE</option>
            <option value="jira">Jira</option>
            <option value="email">E-mail (abre chamado em quase todo ITSM)</option>
            <option value="teams">Microsoft Teams</option>
            <option value="webhook">Webhook (qualquer outro sistema)</option>
          </select>
        </Campo>
      </div>

      {tipo === 'kace_ticket' && (
        <Campo rotulo="ID da fila no KACE" ajuda="O número da fila que deve receber o chamado.">
          <input
            type="number"
            value={campo('filaIdNoKace')}
            onChange={(e) => set('filaIdNoKace', e.target.value)}
          />
        </Campo>
      )}

      {tipo === 'jira' && (
        <>
          {nuvem && (
            <Aviso tipo="alerta">
              <strong>Jira na nuvem.</strong> A instalação precisa de saída para a internet, o que
              contraria a premissa de ambiente fechado do produto. Jira Data Center resolve na rede
              interna. Confirme com o cliente antes de depender deste destino.
            </Aviso>
          )}
          <div className="grade2">
            <Campo rotulo="Endereço">
              <input
                value={campo('baseUrl')}
                placeholder="https://jira.empresa.com.br"
                onChange={(e) => set('baseUrl', e.target.value)}
              />
            </Campo>
            <Campo rotulo="Projeto" ajuda="A chave, não o nome. Ex.: SEG.">
              <input
                value={campo('projeto')}
                placeholder="SEG"
                onChange={(e) => set('projeto', e.target.value)}
              />
            </Campo>
            <Campo rotulo="Tipo de issue">
              <input
                value={campo('tipoIssue')}
                placeholder="Task"
                onChange={(e) => set('tipoIssue', e.target.value)}
              />
            </Campo>
            <Campo rotulo="Usuário">
              <input value={campo('usuario')} onChange={(e) => set('usuario', e.target.value)} />
            </Campo>
            <Campo rotulo="Token de API">
              <input
                type="password"
                value={campo('token')}
                onChange={(e) => set('token', e.target.value)}
              />
            </Campo>
            <Campo
              rotulo="Rótulos"
              ajuda="Separados por vírgula. Espaço vira hífen — o Jira recusa rótulo com espaço."
            >
              <input
                value={campo('rotulos')}
                placeholder="lenanzo, automacao"
                onChange={(e) => set('rotulos', e.target.value)}
              />
            </Campo>
          </div>
        </>
      )}

      {tipo === 'email' && (
        <>
          <Aviso>
            <strong>O conector mais coberto da lista.</strong> Quase todo ITSM abre chamado a partir
            de e-mail — GLPI, Movidesk, Milvus, Freshservice, ServiceNow, Zendesk e o próprio KACE.
            Aponte para o endereço de suporte do cliente e o chamado nasce no sistema que ele já
            usa.
          </Aviso>
          <div className="grade2">
            <Campo rotulo="Servidor SMTP">
              <input
                value={campo('host')}
                placeholder="smtp.empresa.com.br"
                onChange={(e) => set('host', e.target.value)}
              />
            </Campo>
            <Campo rotulo="Segurança">
              <select
                value={campo('seguranca') || 'starttls'}
                onChange={(e) => {
                  const v = e.target.value;
                  const padrao = v === 'tls' ? '465' : v === 'starttls' ? '587' : '25';
                  setC({ ...c, seguranca: v, porta: padrao });
                }}
              >
                <option value="starttls">STARTTLS (587)</option>
                <option value="tls">TLS direto (465)</option>
                <option value="nenhuma">Sem criptografia (25)</option>
              </select>
            </Campo>
            <Campo rotulo="Porta">
              <input
                type="number"
                value={campo('porta') || '587'}
                onChange={(e) => set('porta', e.target.value)}
              />
            </Campo>
            <Campo
              rotulo="Remetente"
              ajuda="Muitos relays só aceitam remetente do próprio domínio."
            >
              <input
                value={campo('remetente')}
                placeholder="plataforma@empresa.com.br"
                onChange={(e) => set('remetente', e.target.value)}
              />
            </Campo>
            <Campo rotulo="Nome do remetente">
              <input
                value={campo('nomeRemetente') || 'Plataforma Lenanzo'}
                onChange={(e) => set('nomeRemetente', e.target.value)}
              />
            </Campo>
            <Campo rotulo="Prefixo do assunto" ajuda="Muitos ITSM roteiam a fila pelo assunto.">
              <input
                value={campo('prefixoAssunto')}
                placeholder="[Lenanzo]"
                onChange={(e) => set('prefixoAssunto', e.target.value)}
              />
            </Campo>
            <Campo
              rotulo="Usuário"
              ajuda="Deixe em branco se o relay interno autorizar por IP — é o caso mais comum."
            >
              <input value={campo('usuario')} onChange={(e) => set('usuario', e.target.value)} />
            </Campo>
            <Campo rotulo="Senha">
              <input
                type="password"
                value={campo('senha')}
                onChange={(e) => set('senha', e.target.value)}
              />
            </Campo>
          </div>
          <Campo rotulo="Destinatários" ajuda="Um por linha.">
            <textarea
              rows={3}
              value={campo('destinatarios')}
              placeholder={'suporte@empresa.com.br\nseguranca@empresa.com.br'}
              onChange={(e) => set('destinatarios', e.target.value)}
            />
          </Campo>
        </>
      )}

      {tipo === 'teams' && (
        <>
          <Aviso tipo="alerta">
            <strong>Não use o "Incoming Webhook" clássico.</strong> Os conectores do Office 365
            foram desativados pela Microsoft em maio de 2026. No Teams, abra o canal →{' '}
            <em>Workflows</em> → "Publicar no canal quando uma requisição de webhook for recebida",
            e use a URL que o fluxo gerar.
          </Aviso>
          <Campo rotulo="URL do fluxo">
            <input
              value={campo('url')}
              placeholder="https://prod-00.brazilsouth.logic.azure.com/workflows/..."
              onChange={(e) => set('url', e.target.value)}
            />
          </Campo>
          <Campo
            rotulo="Link do portal (opcional)"
            ajuda="Vira um botão no cartão, para quem recebe abrir o produto direto."
          >
            <input
              value={campo('urlDoPortal')}
              onChange={(e) => set('urlDoPortal', e.target.value)}
            />
          </Campo>
          <p className="suave" style={{ fontSize: 12.5 }}>
            O cartão aparece com a identidade do Flow bot. Nome e ícone personalizados não são
            suportados por webhook de Workflows — é limitação da plataforma, não do produto.
          </p>
        </>
      )}

      {tipo === 'webhook' && (
        <>
          <Campo rotulo="URL">
            <input
              value={campo('url')}
              placeholder="https://sistema.interno/eventos"
              onChange={(e) => set('url', e.target.value)}
            />
          </Campo>
          <Campo
            rotulo="Segredo HMAC"
            ajuda="Opcional, mas recomendado. O corpo vai assinado em X-Lenanzo-Assinatura — sem isso, quem descobrir a URL abre chamado em nome do produto."
          >
            <input
              type="password"
              value={campo('segredoHmac')}
              onChange={(e) => set('segredoHmac', e.target.value)}
            />
          </Campo>
        </>
      )}

      {/*
        🔴 O que este destino recebe.
        Antes deste filtro, TODO evento ia para TODOS os destinos. Quem
        configurou um canal para software proibido passou a receber aviso de
        SLA e chamado parado quando o vigia foi acrescentado.
      */}
      <h3>O que este destino recebe</h3>
      <p className="suave" style={{ fontSize: 13, margin: '0 0 8px' }}>
        {tiposEscolhidos.size === 0
          ? 'Nada marcado: este destino recebe todos os tipos de evento.'
          : `${tiposEscolhidos.size} tipo(s) marcado(s) — só eles serão enviados.`}
      </p>

      <div className="grade-eventos">
        {TIPOS_DE_EVENTO.map((t) => (
          <label key={t.valor} className="item-evento">
            <input
              type="checkbox"
              checked={tiposEscolhidos.has(t.valor)}
              onChange={(e) => {
                const nova = new Set(tiposEscolhidos);
                if (e.target.checked) nova.add(t.valor);
                else nova.delete(t.valor);
                setTiposEscolhidos(nova);
              }}
            />
            <span>
              {t.rotulo}
              <span className="suave"> · {t.grupo}</span>
            </span>
          </label>
        ))}
      </div>

      <Campo
        rotulo="Severidade mínima"
        ajuda="Eventos abaixo deste nível não são enviados para este destino."
      >
        <select value={severidadeMinima} onChange={(e) => setSeveridadeMinima(e.target.value)}>
          <option value="baixa">Tudo</option>
          <option value="media">Média para cima</option>
          <option value="alta">Alta e crítica</option>
          <option value="critica">Só crítica</option>
        </select>
      </Campo>

      <div className="linha">
        <button className="primario encolhe" disabled={acao.executando} onClick={salvar}>
          Criar destino
        </button>
        <button className="encolhe" onClick={aoCancelar}>
          Cancelar
        </button>
        <span className="cresce" />
      </div>
    </Painel>
  );
}
